import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { approveEdgeTransfer, listEdgeTransfers, rejectEdgeTransfer } from "../api/capabilityApi";
import { getTenants } from "../api/tenantApi";
import { listEdges } from "../api/edgeApi";
import { apiErrorMessage } from "../utils/capabilities";
import { formatDate } from "../utils/i18nFormat";

// Pending cross-tenant edge transfers; the target tenant's admin (edge.transfer) approves or rejects.
// The API returns ids only, so names come from the tenant/edge lists the user can already see
// (an id outside that scope is shown as-is).
export default function EdgeTransfers() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const [rows, setRows] = useState(null);
  const [names, setNames] = useState({ tenants: {}, edges: {} });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    listEdgeTransfers(accessToken, "pending")
      .then((data) => setRows(data.items || data || []))
      .catch((err) => { toast.error(apiErrorMessage(err, t("vpn.transfers.loadFailed"))); setRows([]); });
    Promise.all([
      getTenants(accessToken, { limit: 200, offset: 0 }).then((d) => d.items || d || []).catch(() => []),
      listEdges(accessToken, { limit: 500, offset: 0 }).then((d) => d.items || []).catch(() => []),
    ]).then(([tenants, edges]) => setNames({
      tenants: Object.fromEntries(tenants.map((t) => [t.id, t.name])),
      edges: Object.fromEntries(edges.map((e) => [e.id, e.name || e.edgeId])),
    }));
  }, [accessToken, t]);

  useEffect(() => { load(); }, [load]);

  const act = async (row, fn, okMsg) => {
    setBusyId(row.id);
    try {
      await fn(accessToken, row.id);
      toast.success(okMsg);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      toast.error(apiErrorMessage(err, t("vpn.transfers.actionFailed")));
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (iso) => (iso ? formatDate(iso + (iso.endsWith("Z") ? "" : "Z")) : "—");
  const cell = { color: "var(--text-primary)", borderColor: "var(--border)" };

  return (
    <Box sx={{ color: "var(--text-primary)" }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{t("vpn.transfers.title")}</Typography>
      {rows === null ? (
        <CircularProgress size={22} sx={{ color: "var(--text-muted)" }} />
      ) : rows.length === 0 ? (
        <Typography sx={{ color: "var(--text-muted)" }}>{t("vpn.transfers.empty")}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["device", "sourceTenant", "targetTenant", "requestedBy", "date", ""].map((h) => (
                  <TableCell key={h} sx={{ ...cell, color: "var(--text-muted)", fontWeight: 600 }}>{h && t(`vpn.transfers.col.${h}`)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={cell}>{names.edges[r.edge_id] || r.edge_id}</TableCell>
                  <TableCell sx={cell}>{names.tenants[r.source_tenant_id] || r.source_tenant_id}</TableCell>
                  <TableCell sx={cell}>{names.tenants[r.target_tenant_id] || r.target_tenant_id}</TableCell>
                  <TableCell sx={cell}>{r.requested_by}</TableCell>
                  <TableCell sx={cell}>{fmt(r.requested_at)}</TableCell>
                  <TableCell sx={{ ...cell, whiteSpace: "nowrap" }}>
                    <Button size="small" variant="contained" disabled={busyId === r.id} onClick={() => act(r, approveEdgeTransfer, t("vpn.transfers.approved"))} sx={{ mr: 1, backgroundColor: "var(--btn-primary)" }}>
                      {t("vpn.transfers.approve")}
                    </Button>
                    <Button size="small" variant="outlined" disabled={busyId === r.id} onClick={() => act(r, rejectEdgeTransfer, t("vpn.transfers.rejected"))} sx={{ color: "var(--danger)", borderColor: "var(--border)" }}>
                      {t("vpn.transfers.reject")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
